import { type FC, useEffect, useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { useFilter } from "@/partner/shared/components/Filter";
import { ListPageHeader } from "@/partner/shared/components/ListPageHeader";
import { ProductsAutocomplete } from "@/partner/features/products";
import { DownloadReportButton } from "@/partner/features/reports";
import { ReportTypeType } from "@/types/graphql/partner.generated";
import { buildStreamsFilter } from "../model/buildStreamsFilter";

interface StreamsFilterProps {
  onCreateStream: () => void;
}

export const StreamsFilter: FC<StreamsFilterProps> = ({ onCreateStream }) => {
  const { t } = useTranslation();
  const { filter, onChangeFilter, onClearFilter } = useFilter();

  const selectedProductIds: string[] = filter.productIds ?? [];
  const [opened, setOpened] = useState(selectedProductIds.length > 0);

  // Локальный стейт для дебаунса поиска — не дёргаем onChangeFilter
  // (навигация по URL) на каждое нажатие клавиши, а только через 400мс
  // простоя, как в ContentsFilter/ProductsFilter.
  const [searchInput, setSearchInput] = useState(filter.search ?? "");

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChangeFilter("search", searchInput);
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleClearSearch = () => {
    setSearchInput("");
    onChangeFilter("search", "");
  };

  const handleClearFilter = () => {
    setSearchInput("");
    onClearFilter();
  };

  const isFieldActive = (field: string): boolean => {
    switch (field) {
      case "search":
        return Boolean(filter.search);
      case "productIds":
        return selectedProductIds.length > 0;
      default:
        return false;
    }
  };

  const isVisible = (field: string): boolean => opened || isFieldActive(field);
  const anyVisible = opened || Object.keys(filter).length > 0;
  const showClearButton = Object.keys(filter).length > 0;

  return (
    <ListPageHeader
      title={t("streams.pageTitle")}
      showClearButton={showClearButton}
      onClearFilter={handleClearFilter}
      showFilterToggle
      filtersOpen={opened}
      onToggleFilters={() => setOpened((v) => !v)}
      fieldsVisible={anyVisible}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <DownloadReportButton
            type={ReportTypeType.StreamsType}
            filter={
              buildStreamsFilter(filter) as Record<string, unknown> | undefined
            }
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateStream}
          >
            {t("streams.filter.addButton")}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2} alignItems="center">
        {isVisible("search") && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder={t("streams.filter.searchLabel")}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              InputProps={{
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}

        {isVisible("productIds") && (
          <Grid size={{ xs: 12, md: 6 }}>
            <ProductsAutocomplete
              label={t("streams.filter.productsLabel")}
              value={selectedProductIds}
              onChange={(productIds) =>
                onChangeFilter("productIds", productIds)
              }
            />
          </Grid>
        )}
      </Grid>
    </ListPageHeader>
  );
};
